import React from 'react';
import { MicrophoneStageIcon } from '@phosphor-icons/react';
import { withBlockExtensions } from '@plone/volto/helpers';
import { MPFBlockCardHeader } from 'volto-ministerio-publico-federal/components/UI';

import { RadioCard } from './RadioCard';
import {
  contarEpisodiosInvalidos,
  RADIO_TITULO,
  toEpisodios,
  type RadioData,
} from './helpers';

interface ViewProps {
  data: RadioData;
  isEditMode?: boolean;
  /** Presente só no editor: abre o episódio de índice N na barra lateral. */
  onEditItem?: (index: number) => void;
}

/* `not-prose` desliga a tipografia de texto corrido que a página herda do
   editor (`[class*='__editor-content']`, em `base/_editor-content.scss`). */
const ROOT_CLASS = 'mpf-podcast not-prose';

const Aviso: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-podcast__aviso">{children}</p>
);

/**
 * Faixa de episódios de rádio/podcast.
 *
 * O cabeçalho nasce DESLIGADO porque o uso principal é logo abaixo da faixa de
 * vídeos, cujo "Rádio e TV" já nomeia as duas — ligá-lo repetiria o título na
 * tela. Sozinha em outra página, o editor liga a chave.
 */
export const MpfRadioBlockView: React.FC<ViewProps> = ({
  data,
  isEditMode,
  onEditItem,
}) => {
  const tituloId = React.useId();
  const cards = React.useMemo(
    () => toEpisodios(data?.episodios),
    [data?.episodios],
  );
  const invalidos = contarEpisodiosInvalidos(data?.episodios);

  const titulo = data?.titulo?.trim() || RADIO_TITULO;
  const cabecalho = data?.exibirTitulo ? (
    <MPFBlockCardHeader
      icon={
        <MicrophoneStageIcon
          weight="fill"
          aria-hidden="true"
          className="h-6 w-6 shrink-0 text-teal-600"
        />
      }
      title={titulo}
      titleId={tituloId}
    />
  ) : null;

  if (cards.length === 0) {
    // No site, faixa sem episódio utilizável simplesmente não existe.
    if (!isEditMode) return null;

    return (
      <section className={ROOT_CLASS}>
        {cabecalho}
        <Aviso>
          {invalidos > 0
            ? 'Nenhum episódio tem arquivo de áudio. Escolha o arquivo de cada um na barra lateral.'
            : 'Escolha os episódios da faixa na barra lateral.'}
        </Aviso>
      </section>
    );
  }

  return (
    <section
      className={ROOT_CLASS}
      {...(cabecalho
        ? { 'aria-labelledby': tituloId }
        : { 'aria-label': titulo })}
    >
      {cabecalho}

      <div className="mpf-podcast__grid">
        {cards.map((card) => (
          <RadioCard key={card.key} card={card} onEditItem={onEditItem} />
        ))}
      </div>

      {isEditMode && invalidos > 0 && (
        <Aviso>
          {invalidos === 1
            ? 'Um episódio não aparece: falta o arquivo de áudio.'
            : `${invalidos} episódios não aparecem: falta o arquivo de áudio.`}
        </Aviso>
      )}
    </section>
  );
};

export default withBlockExtensions(MpfRadioBlockView);
